import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@repletoryreach/sdk-auth';
import { identity, billing } from '@repletoryreach/sdk-api';
import { listTimezones } from '@repletoryreach/sdk-utils/date';
import { Stack, Card, Title, Button, Text, Group, Badge } from '@repletoryreach/sdk-ui';
import { TextInput, Select, CountryPicker } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { useWallet } from '../../../data/useWallet';
import { phoneLabels } from '../../../lib/phone';
import { AccountCards } from './AccountCards';
import { BusinessHoursCard } from './BusinessHoursCard';

/**
 * Countries the platform can actually bill in.
 *
 * §4 item 4: this was hardcoded to three, so a tenant whose `country_iso` was
 * anything else had no correct option and Save quietly rewrote it to `ZA`. The
 * org's own value is added to the list below whatever it happens to be, so the
 * page can never silently change it.
 */
const COUNTRIES = [
  { value: 'ZA', labelKey: 'country.za' },
  { value: 'NG', labelKey: 'country.ng' },
  { value: 'KE', labelKey: 'country.ke' }
];

/** What each country decides, so the consequence of changing it is concrete. */
const COUNTRY_EFFECTS: Record<string, { currency: string; gateway: string }> = {
  ZA: { currency: 'ZAR', gateway: 'PayFast' },
  NG: { currency: 'NGN', gateway: 'Paystack' },
  KE: { currency: 'KES', gateway: '–' }
};

const NAME_MAX = 120;

export default function SettingsOrganisationPage() {
  const t = useT();
  const { user, refresh } = useAuth();
  const walletQ = useWallet();

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('Africa/Johannesburg');
  const [country, setCountry] = useState('ZA');
  // The country assumed for a phone number typed without a code. Empty means
  // "follow the billing country", which is what the service stores as null.
  const [defaultCountry, setDefaultCountry] = useState('');
  const [busy, setBusy] = useState(false);
  const [nameError, setNameError] = useState('');

  /*
   * §4 item 2: `useState(user?.org.name ?? '')` runs once and `AuthProvider`
   * fills `user` asynchronously, so on a hard refresh straight onto this page
   * the name initialised to `''`, never re-seeded, and Save wrote that empty
   * string over the real org name.
   */
  useEffect(() => {
    if (!user) return;
    setName(user.org.name ?? '');
    setTimezone(user.org.timezone ?? 'Africa/Johannesburg');
    setCountry(user.org.country_iso ?? 'ZA');
    setDefaultCountry(user.org.default_country ?? '');
  }, [user?.org.name, user?.org.timezone, user?.org.country_iso, user?.org.default_country]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * §4 item 3: four hardcoded timezones, so a tenant on `Africa/Accra` saw a
   * blank Select and Save rewrote it to Johannesburg. The full IANA list comes
   * from `Intl.supportedValuesOf`, and the org's current value is always in
   * the options even if the runtime does not list it.
   */
  const timezones = useMemo(() => {
    const all = listTimezones();
    const current = user?.org.timezone;
    return current && !all.includes(current) ? [current, ...all] : all;
  }, [user?.org.timezone]);

  const countries = useMemo(() => {
    const current = user?.org.country_iso;
    const known = COUNTRIES.map((c) => c.value);
    return current && !known.includes(current)
      ? [...COUNTRIES, { value: current, labelKey: `country.${current.toLowerCase()}` }]
      : COUNTRIES;
  }, [user?.org.country_iso]);

  const dirty = !!user && (
    name !== (user.org.name ?? '')
    || timezone !== (user.org.timezone ?? 'Africa/Johannesburg')
    || country !== (user.org.country_iso ?? 'ZA')
    || defaultCountry !== (user.org.default_country ?? '')
  );
  const countryChanged = !!user && country !== (user.org.country_iso ?? 'ZA');

  async function persist() {
    setBusy(true);
    try {
      await identity.updateOrganisation({ name: name.trim(), timezone, country_iso: country, default_country: defaultCountry });
      await refresh();
      notifySuccess(t('settings.organisation.toast.saved'), t('settings.nav.organisation'));
    } catch (e) {
      // §4 item 10: the two toasts had different titles.
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.organisation'));
    } finally {
      setBusy(false);
    }
  }

  function save(e?: React.FormEvent) {
    e?.preventDefault();
    // §4 item 6: empty, whitespace-only and 500-character names all submitted.
    const trimmed = name.trim();
    if (trimmed.length === 0) { setNameError(t('settings.organisation.nameRequired')); return; }
    if (trimmed.length > NAME_MAX) { setNameError(t('settings.organisation.nameTooLong', { max: NAME_MAX })); return; }

    /*
     * §4 item 1: changing the country re-points currency, payment gateway and
     * tax profile, and a misclick can leave a tenant with no working way to
     * pay at all (KE has only the placeholder Stripe checkout). It had no
     * confirmation.
     */
    if (countryChanged) {
      const effect = COUNTRY_EFFECTS[country];
      openConfirmModal({
        title: t('settings.organisation.country.confirm.title'),
        children: (
          <Stack gap={8}>
            <Text role="bodySmall">
              {t('settings.organisation.country.confirm.body', {
                country: t(`country.${country.toLowerCase()}`, country),
                currency: effect?.currency ?? country
              })}
            </Text>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('settings.organisation.country.confirm.hint', { gateway: effect?.gateway ?? '–' })}
            </Text>
          </Stack>
        ),
        labels: { confirm: t('action.saveChanges'), cancel: t('action.cancel') },
        confirmProps: { color: 'red' },
        onConfirm: () => void persist()
      });
      return;
    }

    void persist();
  }

  const effect = COUNTRY_EFFECTS[country];

  return (
    <Stack gap="md">
    <Card withBorder data-guide="org-details">
      {/* §4 item 7: not a form, so Enter did nothing. */}
      <form onSubmit={save}>
        <Stack gap="md">
          <Title order={4}>{t('settings.nav.organisation')}</Title>

          <TextInput
            // §4 item 11: the label borrowed a key from the signup form.
            label={t('settings.organisation.name')}
            description={t('settings.organisation.nameHint')}
            error={nameError}
            value={name}
            onChange={(e) => { setName(e.currentTarget.value); setNameError(''); }}
            maxLength={NAME_MAX}
            required
          />

          {/* §4 item 5: the slug was neither shown nor explained, though it is
              what appears in links and invitations. */}
          {user?.org.slug && (
            <Stack gap={2}>
              <Text role="inputLabel">{t('settings.organisation.slug')}</Text>
              <Group gap="xs" align="center">
                <code>{user.org.slug}</code>
                <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.organisation.slugHint')}</Text>
              </Group>
            </Stack>
          )}

          <Select
            label={t('settings.organisation.country')}
            // §4 item 12: the hint was a loose Text below both selects rather
            // than attached to the control it describes.
            description={t('settings.organisation.countryHint')}
            value={country}
            onChange={(e) => setCountry(e.currentTarget.value)}
            data={countries.map((c) => ({ value: c.value, label: t(c.labelKey, c.value) }))}
          />

          {/* §4 item 9: the hint promised consequences and showed none. */}
          <Group gap="xs" wrap="wrap">
            <Badge size="xs" variant="light">
              {t('settings.organisation.effect.currency', {
                currency: walletQ.data?.data.currency ?? effect?.currency ?? '–'
              })}
            </Badge>
            <Badge size="xs" variant="light">
              {t('settings.organisation.effect.gateway', { gateway: effect?.gateway ?? '–' })}
            </Badge>
          </Group>

          <CountryPicker
            label={t('settings.organisation.defaultCountry')}
            description={
              defaultCountry
                ? t('settings.organisation.defaultCountryHint')
                : `${t('settings.organisation.defaultCountrySame')} ${t('settings.organisation.defaultCountryHint')}`
            }
            // Shows the billing country while nothing else is chosen; choosing
            // the billing country again clears the override.
            value={defaultCountry || country}
            onChange={(iso) => setDefaultCountry(iso === country ? '' : iso)}
            labels={phoneLabels(t)}
          />

          <Select
            label={t('settings.organisation.timezone')}
            description={t('settings.organisation.timezoneHint')}
            value={timezone}
            onChange={(e) => setTimezone(e.currentTarget.value)}
            data={timezones.map((tz) => ({ value: tz, label: tz }))}
          />

          <div>
            <Button type="submit" loading={busy} disabled={!dirty}>{t('action.saveChanges')}</Button>
          </div>
        </Stack>
      </form>
    </Card>
    {/* Weekly hours, closed days and their time zone (7 October 2026). */}
    <BusinessHoursCard />
    {/* Export everything and close the account (2 October 2026). */}
    <AccountCards />
    </Stack>
  );
}
